
import MeetHourJS from '../lib-meet-hour';
import Platform from '../react/Platform';

import { isMobileBrowser } from './utils';

const { browser } = MeetHourJS.util;

const DEFAULT_OPTIMAL_BROWSERS = [
    'chrome',
    'chromium',
    'electron',
    'firefox',
    'nwjs',
    'safari',
    'webkit'
];

const DEFAULT_UNSUPPORTED_BROWSERS = [];

const browserNameToCheck = {
    chrome: browser.isChrome.bind(browser),
    chromium: browser.isChromiumBased.bind(browser),
    electron: browser.isElectron.bind(browser),
    firefox: browser.isFirefox.bind(browser),
    safari: browser.isSafari.bind(browser),
    webkit: browser.isWebKitBased.bind(browser)
};


declare var interfaceConfig: Object;

/**
 * Returns whether or not jitsi is optimized and targeted for the  provided
 * browser name.
 *
 * @param {string} browserName - The name of the browser to check.
 * @returns {boolean}
 */
export function isBrowsersOptimal(browserName: string) {
    return (interfaceConfig.OPTIMAL_BROWSERS || DEFAULT_OPTIMAL_BROWSERS)
        .includes(browserName);
}

/**
 * Returns whether or not the current browser or the list of passed in browsers
 * is considered suboptimal. Suboptimal means it is a supported browser but has
 * not been explicitly listed as being optimal, possibly due to functionality
 * issues.
 *
 * @param {Array<string>} [browsers] - A list of browser names to check. Will
 * default to a whitelist.
 * @returns {boolean}
 */
export function isSuboptimalBrowser() {
    const optimalBrowsers
        = interfaceConfig.OPTIMAL_BROWSERS || DEFAULT_OPTIMAL_BROWSERS;

    return !_isCurrentBrowserInList(optimalBrowsers) && isSupportedBrowser();
}

/**
 * Returns whether or not the current browser should allow the app to display.
 * A supported browser is assumed to be able to support WebRtc.
 *
 * @returns {boolean}
 */
export function isSupportedBrowser() {
    if (navigator.product === 'ReactNative') {
        return false;
    }

    // Blacklists apply to desktop browsers only right now.
    if (!isMobileBrowser() && _isCurrentBrowserInList(
        interfaceConfig.UNSUPPORTED_BROWSERS || DEFAULT_UNSUPPORTED_BROWSERS
    )) {
        return false;
    }

    // We are intentionally allow mobile browsers because:
    // - the WelcomePage is mobile ready;
    // - if the URL points to a conference then deep-linking will take
    //   care of it.
    return isMobileBrowser() || MeetHourJS.isWebRtcSupported();
}

/**
 * Returns whether or not the current environment is a supported
 * browser on a mobile device.
 *
 * @returns {boolean}
 */
export function isSupportedMobileBrowser() {
    return (interfaceConfig.ENABLE_MOBILE_BROWSER === true)
    && ((Platform.OS === 'android' && browser.isChromiumBased())
        || (Platform.OS === 'android' && browser.isFirefox())
        || (Platform.OS === 'ios' && browser.isWebKitBased()));
}

/**
 * Runs various browser checks to know if the current browser is found within
 * the list.
 *
 * @param {Array<string>} list - Browser names to check. The names should be
 * keys in {@link browserNameToCheck}.
 * @private
 * @returns {boolean}
 */
function _isCurrentBrowserInList(list) {
    return Boolean(list.find(browserName => {
        const checkFunction = browserNameToCheck[browserName];

        return checkFunction ? checkFunction.call(browser) : false;
    }));
}


/**
 * Returns whether or not the current OS is Windows.
 *
 * @returns {boolean}
 */
export function isWindows() {
    return Platform.OS === 'windows';
}

/**
 * Returns whether or not the current Device is in Lanscape Mode.
 *
 * @returns {boolean}
 */
export function isDeviceLandScape() {
    return window.matchMedia('(orientation: landscape) ').matches && (window.innerWidth < 765 || window.innerHeight < 380) && window.innerWidth > window.innerHeight;
}

/**
 * Returns whether or not the current device is a native mobile device.
 *
 * @returns {boolean}
 */
export function isMobileNative() {
    return window.matchMedia('(pointer: coarse) and (hover: none)').matches && window.innerWidth < 500;
}

/**
 *
 * @param {*} background
 * @returns
 */
export function extractHsl(background) {
    return background.substring(background.lastIndexOf('hsl('));
}

/**
 *
 * @param {*} hslColor
 * @returns
 */
export function addTileGradients(hslColor) {
    const TILE_GRADIENTS
  = 'radial-gradient(circle at 16% 40%, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0) 40%), '
  + 'radial-gradient(circle at 58% 65%, rgba(121, 121, 121, 0.25), transparent 50%),';

    return `${TILE_GRADIENTS}${hslColor}`;
}
